import { Vector3 } from 'three';
import { ImageCenteredElement } from '@thive/canvas';
import { UxOnly } from '@thive/fddm-types';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { IAppService } from '@/modules/app/service/IAppService';
import { FileServiceConnector, IFileServiceConnector } from '@/modules/api/FileApiConnector';
import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import { LayerNames } from '@/modules/common/types/layers';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { useCanvasStore } from '@/modules/canvas/store';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import { METERS_TO_MILLIMETERS_FACTOR } from '@/modules/canvas/factories/element';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { logger } from '@common/logging/logger';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import { PROJECT_FILES_ROOT_CONTAINER, REFERENCE_IMAGE_FILE_CONTAINER } from '@/modules/api/constant';
import { referenceImageFileNameUtils } from '@/modules/referenceImage/utils';
import { LayerServiceFactory } from '@/modules/layers/service/LayerServiceFactory';
import { ILayerService } from '@/modules/layers/service/ILayerService';

import {
  REFERENCE_IMAGE_ELEMENT_ID,
  INITIAL_GAP_TO_AXIS_IN_MM,
  REFERENCE_IMAGE_CANVAS_ELEMENT_NAME,
  REFERENCE_OPACITY_DEFAULT,
  REFERENCE_CANVAS_OPACITY_TO_OPACITY_PERCENT_FACTOR,
  REFERENCE_IMAGE_UPLOAD_MAX_SIZE_BYTES,
  REFERENCE_POINT_A_ELEMENT_ID,
  REFERENCE_POINT_B_ELEMENT_ID,
} from '../constants';
import { isValidReferenceFileType } from '../guards';
import { mapFileTypeToFileExtension } from '../mappers';
import { useReferenceImageUIStore } from '../store';
import { IReferenceImageService } from './IReferenceImageService';
import {
  buildScaleCrossElements as buildReferenceScaleCrossElements,
  buildScaleLineElement as buildReferenceScaleLineElement,
} from '../helpers';

export class ReferenceImageServiceImpl implements IReferenceImageService {
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private canvasStore: typeof useCanvasStore;
  private referenceImageUIStore: typeof useReferenceImageUIStore;
  private canvasSvc: ICanvasService;
  private fileSvc: IFileServiceConnector;
  private fddmSvc: IFddmService;
  private layerSvc: ILayerService;

  public constructor(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    canvasStore: typeof useCanvasStore,
    referenceImageUIStore: typeof useReferenceImageUIStore,
  ) {
    this.floorplanDesignStore = floorplanDesignStore;
    this.canvasStore = canvasStore;
    this.referenceImageUIStore = referenceImageUIStore;
    this.canvasSvc = CanvasServiceFactory.getInstance().createCanvasService();
    this.fileSvc = FileServiceConnector.getInstance();
    this.fddmSvc = FddmServiceFactory.getInstance().createFddmService();
    this.layerSvc = LayerServiceFactory.getInstance().createLayerService();
  }

  uploadReferenceImage(file: File) {
    const { referenceImageActions } = this.referenceImageUIStore.getState();
    referenceImageActions.setUploadProcess({ status: 'loading' });

    const fileType = file.type;

    // Validation
    if (!isValidReferenceFileType(fileType)) {
      referenceImageActions.setUploadProcess({
        status: 'error',
        code: 'incompatible_file',
      });

      logger.error('Unexpected reference image file type. Filetype: ', fileType);

      return;
    }

    if (file.size > REFERENCE_IMAGE_UPLOAD_MAX_SIZE_BYTES) {
      referenceImageActions.setUploadProcess({
        status: 'error',
        code: 'file_too_large',
      });
      referenceImageActions.setMode('upload');

      return;
    }

    this.layerSvc.setLayerVisibilityMode(LayerNames.REFERENCE, 'force_show');

    const fileDisplayName = file.name.split('.')[0];
    const fileRef = `${REFERENCE_IMAGE_FILE_CONTAINER}/${file.name}`;

    const image = new Image();
    const dataUrl = URL.createObjectURL(file);

    const onloadCallback = async () => {
      const imageWidth = image.naturalWidth;
      const imageHeight = image.naturalHeight;
      const centerPositionInMm = new Vector3(
        imageWidth / 2 + INITIAL_GAP_TO_AXIS_IN_MM,
        imageHeight / 2 + INITIAL_GAP_TO_AXIS_IN_MM,
        Z_INDEX_MAP[FDDM_TAG.REFERENCE_IMAGE],
      );

      this.pushReferenceImageToCanvas({
        image,
        width: imageWidth,
        height: imageHeight,
        centerPositionInMm,
        opacity: REFERENCE_OPACITY_DEFAULT / REFERENCE_CANVAS_OPACITY_TO_OPACITY_PERCENT_FACTOR,
      });

      // Save file remotely
      try {
        const basePath = buildRevisionFileStorageBasePath();
        if (!basePath) throw Error('basePath not defined.');

        await this.fileSvc.addFile(
          `${basePath}/${fileRef}`,
          file,
          mapFileTypeToFileExtension(fileType),
          PROJECT_FILES_ROOT_CONTAINER,
        );
      } catch (error) {
        referenceImageActions.setUploadProcess({
          status: 'error',
          code: 'save_failed',
        });

        referenceImageActions.setMode('upload');
      }

      // Update fddm model
      const centerPositionInMeter = centerPositionInMm
        .clone()
        .divideScalar(METERS_TO_MILLIMETERS_FACTOR);
      const rectangleLengthInMeter = imageWidth / METERS_TO_MILLIMETERS_FACTOR;
      const rectangleWidthInMeter = imageHeight / METERS_TO_MILLIMETERS_FACTOR;

      this.fddmSvc.addReferenceImage({
        opacity: REFERENCE_OPACITY_DEFAULT,
        imageReference: fileRef,
        rectangle: {
          centerX: centerPositionInMeter.x,
          centerY: centerPositionInMeter.y,
          width: rectangleWidthInMeter,
          length: rectangleLengthInMeter,
          angle: 0,
        },
      });

      // Update UI
      referenceImageActions.setUploadProcess({ status: 'success' });
      referenceImageActions.setOriginalDimensions({ width: imageWidth, height: imageHeight });
      referenceImageActions.setFileDisplayName(fileDisplayName);
      referenceImageActions.setMode('none');

      this.canvasSvc.zoomToFitElements([REFERENCE_IMAGE_ELEMENT_ID]);
      this.appSvc.activateTool('scale_reference');

      URL.revokeObjectURL(dataUrl);
    };

    image.onload = onloadCallback;
    image.onerror = () => {
      referenceImageActions.setUploadProcess({
        status: 'error',
        code: 'image_processing_failed',
      });
      referenceImageActions.setMode('upload');
      URL.revokeObjectURL(dataUrl);
    };

    image.src = dataUrl;
  }

  async loadReferenceImage(data: UxOnly['referenceImage']): Promise<void> {
    if (!data) {
      return;
    }

    const { referenceImageActions } = this.referenceImageUIStore.getState();
    const basePath = buildRevisionFileStorageBasePath();
    if (!basePath) {
      logger.error('Could not load reference image. basePath not defined.');
      return;
    }

    const file = await this.fileSvc.getBlobFileByCategory(
      `${basePath}/${data.imageReference}`,
      PROJECT_FILES_ROOT_CONTAINER,
    );

    if (!file) {
      logger.error('Could not load reference image. File not found:', data.imageReference);
      return;
    }

    const image = new Image();

    const onloadCallback = async () => {
      const { naturalWidth, naturalHeight } = image;
      const referenceElementWidth = data.rectangle.length * METERS_TO_MILLIMETERS_FACTOR;
      const referenceElementHeight = data.rectangle.width * METERS_TO_MILLIMETERS_FACTOR;
      const centerPositionInMm = new Vector3(
        data.rectangle.centerX * METERS_TO_MILLIMETERS_FACTOR,
        data.rectangle.centerY * METERS_TO_MILLIMETERS_FACTOR,
        Z_INDEX_MAP[FDDM_TAG.REFERENCE_IMAGE],
      );

      this.pushReferenceImageToCanvas({
        image,
        width: referenceElementWidth,
        height: referenceElementHeight,
        centerPositionInMm,
        opacity: data.opacity / REFERENCE_CANVAS_OPACITY_TO_OPACITY_PERCENT_FACTOR,
      });

      // Update UI
      referenceImageActions.setUploadProcess({ status: 'success' });
      referenceImageActions.setOriginalDimensions({ width: naturalWidth, height: naturalHeight });
      referenceImageActions.setFileDisplayName(
        referenceImageFileNameUtils.getDisplayNameFromPersistedFileRef(data.imageReference),
      );
      referenceImageActions.setMode('none');
    };

    image.onload = onloadCallback;
    const dataUrl = URL.createObjectURL(file);
    image.src = dataUrl;
  }

  getReferenceImage() {
    return this.floorplanDesignStore.getState().uxonly.referenceImage;
  }

  startScalingLifecycle() {
    // Prep UI and draft state
    const { referenceImageActions } = this.referenceImageUIStore.getState();
    referenceImageActions.setRequiredScalingDialogOpenState(true);
    referenceImageActions.setMode('scale');
    referenceImageActions.setScaleDraft({
      distanceBetweenPoints: null,
      pointA: null,
      pointB: null,
    });
  }

  endScalingLifecycle() {
    // Reset UI
    const { referenceImageActions } = this.referenceImageUIStore.getState();
    referenceImageActions.setRequiredScalingDialogOpenState(false);
    referenceImageActions.setMode('none');
    referenceImageActions.setScaleDraft(null);

    // Remove scaling elements from canvas
    this.canvasSvc.removeCanvasElements({
      method: 'byTag',
      tags: [
        FDDM_TAG.REFERENCE_SCALING_LINE,
        FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A,
        FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B,
      ],
    });

    this.canvasSvc.setSelection([]);
    this.appSvc.setSelectedIds([]);
    this.appSvc.resetTool();
    this.canvasSvc.zoomToFit();
  }

  scaleReferenceImage(params: {
    canvasPointA: { x: number; y: number };
    canvasPointB: { x: number; y: number };
    realDistanceBetweenPointsInMm: number;
  }) {
    const { canvasPointA, canvasPointB, realDistanceBetweenPointsInMm } = params;
    const { referenceImage } = this.floorplanDesignStore.getState().uxonly;
    const currentDimensions = referenceImage?.rectangle;

    if (!referenceImage || !currentDimensions) {
      logger.error('Expected referenceImage dimensions to be defined.');
      return;
    }

    // Update data model
    const distance = Math.hypot(canvasPointB.x - canvasPointA.x, canvasPointB.y - canvasPointA.y);
    const scaleFactor = realDistanceBetweenPointsInMm / distance;
    const newRectangleWidthInMeter = currentDimensions.width * scaleFactor;
    const newRectangleLengthInMeter = currentDimensions.length * scaleFactor;

    this.fddmSvc.updateReferenceImage({
      ...referenceImage,
      rectangle: {
        ...referenceImage.rectangle,
        width: newRectangleWidthInMeter,
        length: newRectangleLengthInMeter,
      },
    });

    // Update element in canvas
    const referenceImageElement = this.canvasStore
      .getState()
      .instance?.getElement(REFERENCE_IMAGE_ELEMENT_ID);
    if (!referenceImageElement) {
      logger.error('Expected to find a reference image element in the canvas.');
      return;
    }
    if (referenceImageElement.type !== 'ImageCentered') {
      logger.error(
        'Expected the reference image element to be of type "ImageCentered". Got: ',
        referenceImageElement.type,
      );
      return;
    }
    const imageElement: ImageCenteredElement = {
      ...referenceImageElement,
      width: newRectangleLengthInMeter * METERS_TO_MILLIMETERS_FACTOR,
      height: newRectangleWidthInMeter * METERS_TO_MILLIMETERS_FACTOR,
    };
    this.canvasSvc.updateElements({ referenceImage: imageElement });

    // Handle needed UI changes and cleanup
    this.endScalingLifecycle();
  }

  updateScalePointA(point: { x: number; y: number }) {
    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;

    const {
      referenceImageActions,
      referenceImage: { scaleDraft },
    } = useReferenceImageUIStore.getState();

    referenceImageActions.setScaleDraft((current) => {
      if (!current) {
        logger.error('Expected scaleDraft to be defined in updater');
        return current;
      }

      return {
        ...current,
        pointA: {
          x: point.x,
          y: point.y,
        },
      };
    });

    // Render pointA
    canvas.updateElements(
      buildReferenceScaleCrossElements(REFERENCE_POINT_A_ELEMENT_ID, canvas.getZoom(), point),
    );

    if (scaleDraft?.pointB) {
      // Render line between pointA and pointB
      const line = buildReferenceScaleLineElement(point, scaleDraft.pointB);
      canvas.updateElement(line);
    }
  }

  updateScalePointB(point: { x: number; y: number }) {
    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;

    const {
      referenceImageActions,
      referenceImage: { scaleDraft },
    } = useReferenceImageUIStore.getState();

    referenceImageActions.setScaleDraft((current) => {
      if (!current) {
        logger.error('Expected scaleDraft to be defined in updater');
        return current;
      }

      return {
        ...current,
        pointB: {
          x: point.x,
          y: point.y,
        },
      };
    });

    // Render pointB
    canvas.updateElements(
      buildReferenceScaleCrossElements(REFERENCE_POINT_B_ELEMENT_ID, canvas.getZoom(), point),
    );

    if (scaleDraft?.pointA) {
      // Render line between pointA and pointB
      const line = buildReferenceScaleLineElement(scaleDraft.pointA, point);
      canvas.updateElement(line);
    }
  }

  startMovingLifecycle() {
    // Prep UI
    this.referenceImageUIStore.getState().referenceImageActions.setMode('move');

    // Make canvas referenceimage element draggable
    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;
    const currentElement = canvas.getElement(REFERENCE_IMAGE_ELEMENT_ID);
    if (!currentElement) {
      logger.error('Could not find reference image element in the canvas');
      return;
    }

    // Keep TS happy
    if (currentElement.type !== 'ImageCentered') {
      logger.error(
        'Expected the reference image element to be of type "ImageCentered". Got: ',
        currentElement.type,
      );
      return;
    }

    const updatedElement = {
      ...currentElement,
      interactivity: {
        ...currentElement.interactivity,
        selectable: true,
        draggable: true,
      },
    };

    this.canvasSvc.updateElements({
      referenceImage: updatedElement,
    });
  }

  endMovingLifecycle() {
    // Reset UI
    this.referenceImageUIStore.getState().referenceImageActions.setMode('none');

    const referenceElementCurrent = this.canvasStore
      .getState()
      .instance?.getElement(REFERENCE_IMAGE_ELEMENT_ID);
    if (!referenceElementCurrent) {
      logger.error('Expected to find a reference image element in the canvas.');
      return;
    }
    if (referenceElementCurrent.type !== 'ImageCentered') {
      logger.error(
        'Expected the reference image element to be of type "ImageCentered". Got: ',
        referenceElementCurrent.type,
      );
      return;
    }

    const referenceSpatial = this.floorplanDesignStore.getState().uxonly.referenceImage?.rectangle;
    if (!referenceSpatial) {
      logger.error('---- expected referenceImage.rectangle to be defined.');
      return;
    }

    // Set canvas element to currently stored position (after user confirmed or cancelled)
    // and make it non interactive again
    const centerPositionInMm = new Vector3(
      referenceSpatial.centerX * METERS_TO_MILLIMETERS_FACTOR,
      referenceSpatial.centerY * METERS_TO_MILLIMETERS_FACTOR,
      Z_INDEX_MAP[FDDM_TAG.REFERENCE_IMAGE],
    );
    const imageElement: ImageCenteredElement = {
      ...referenceElementCurrent,
      position: centerPositionInMm,
      interactivity: {
        ...referenceElementCurrent.interactivity,
        draggable: false,
        selectable: false,
      },
    };
    this.canvasSvc.updateElements({ referenceImage: imageElement });
    this.canvasSvc.setSelection([]);
    this.appSvc.setSelectedIds([]);

    // Reset tool
    this.appSvc.resetTool();
  }

  moveReferenceImage(
    newOriginInMeter: { centerX: number; centerY: number },
    updateCanvasElement: boolean,
  ) {
    const { referenceImage } = this.floorplanDesignStore.getState().uxonly;

    if (!referenceImage) {
      logger.error('Expected referenceImage dimensions to be defined.');
      return;
    }

    this.fddmSvc.updateReferenceImage({
      ...referenceImage,
      rectangle: {
        ...referenceImage.rectangle,
        centerX: newOriginInMeter.centerX,
        centerY: newOriginInMeter.centerY,
      },
    });

    // Update element in canvas
    if (updateCanvasElement) {
      const referenceImageElement = this.canvasStore
        .getState()
        .instance?.getElement(REFERENCE_IMAGE_ELEMENT_ID);

      if (!referenceImageElement) {
        logger.error('Expected to find a reference image element in the canvas.');
        return;
      }
      if (referenceImageElement.type !== 'ImageCentered') {
        logger.error(
          'Expected the reference image element to be of type "ImageCentered". Got: ',
          referenceImageElement.type,
        );
        return;
      }

      const centerPositionInMm = new Vector3(
        newOriginInMeter.centerX * METERS_TO_MILLIMETERS_FACTOR,
        newOriginInMeter.centerY * METERS_TO_MILLIMETERS_FACTOR,
        Z_INDEX_MAP[FDDM_TAG.REFERENCE_IMAGE],
      );
      const imageElement: ImageCenteredElement = {
        ...referenceImageElement,
        position: centerPositionInMm,
      };
      this.canvasSvc.updateElements({ referenceImage: imageElement });
    }
  }

  updateReferenceImageOpacity(value: number) {
    let validated = value;
    if (value > 100) {
      validated = 100;
    } else if (value < 0) {
      validated = 0;
    } else {
      validated = Math.round(value);
    }

    // Update model
    const current = this.fddmSvc.getReferenceImage();
    if (!current) return;
    this.fddmSvc.updateReferenceImage({ ...current, opacity: validated });

    // Update canvas
    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;
    const currentElement = canvas.getElement(REFERENCE_IMAGE_ELEMENT_ID);
    if (!currentElement) {
      logger.error('Could not find reference image element in the canvas');
      return;
    }
    // Keep TS happy
    if (currentElement.type !== 'ImageCentered') {
      logger.error(
        'Expected the reference image element to be of type "ImageCentered". Got: ',
        currentElement.type,
      );
      return;
    }
    this.canvasSvc.updateElements({
      referenceImage: {
        ...currentElement,
        opacity: validated / REFERENCE_CANVAS_OPACITY_TO_OPACITY_PERCENT_FACTOR,
      },
    });
  }

  deleteReferenceImage() {
    this.fddmSvc.deleteReferenceImage();
    this.canvasSvc.removeCanvasElements({
      method: 'byId',
      ids: [REFERENCE_IMAGE_ELEMENT_ID],
    });
    this.referenceImageUIStore
      .getState()
      .referenceImageActions.setUploadProcess({ status: 'idle' });

    /*
      NOTE: file is not deleted from file api on purpose. Functioning as a soft-delete
    */
  }

  private pushReferenceImageToCanvas(params: {
    width: number;
    height: number;
    image: HTMLImageElement;
    centerPositionInMm: Vector3;
    opacity: number;
  }) {
    const imageElement: ImageCenteredElement = {
      type: 'ImageCentered',
      id: REFERENCE_IMAGE_ELEMENT_ID,
      name: REFERENCE_IMAGE_CANVAS_ELEMENT_NAME,
      width: params.width,
      height: params.height,
      image: params.image,
      interactivity: INTERACTIVITY_OFF,
      position: params.centerPositionInMm,
      opacity: params.opacity,
      tags: [FDDM_TAG.REFERENCE_IMAGE, FDDM_TAG.LAYER_REFERENCE],
    };

    this.canvasSvc.updateElements({
      referenceImage: imageElement,
    });
  }

  private get appSvc(): IAppService {
    return AppServiceFactory.getInstance().createAppService();
  }
}
